import { StatusChip } from "@/components/StatusChip";
import { Column } from "@/constants/pipeline";
import { CircleCheck2Icon } from "@/icons";
import { formatDateDaysOld, formatNewActivity, formatNewActivityShort } from "@/lib/utils";
import { CustomerModel } from "@/models/pipeline";
import { ActivityHover } from "./ActivityHover";
import { ApplicationDropdown } from "./ApplicationDropdown";
import { FirstColumnContent } from "./FirstColumnContent";
import { LastNoteTooltip } from "./LastNoteTooltip";
import { Row } from "./Row";
import { RowContent } from "./RowContent";

export type CustomerRowProps = {
  data: CustomerModel;
  agentId: number;
  columns: Column[];
  isPinned: boolean;
  isDepartmentView?: boolean;
  showPin?: boolean;
  onAgentNameClick?: (agentName: string) => void;
  onTogglePin: (id: string) => void;
};

export const CustomerRow = ({
  data,
  agentId,
  columns,
  isPinned,
  isDepartmentView = false,
  showPin = true,
  onAgentNameClick,
  onTogglePin,
}: CustomerRowProps) => {
  const hasAgentColumn = columns.some((column) => column.key === "agentName");

  return (
    <Row id={data.applicationId.toString()} data={data}>
      <RowContent
        columns={columns}
        children={[
          <FirstColumnContent
            itemId={data.applicationId.toString()}
            agentId={agentId}
            isPinned={isPinned}
            handoverAllocatedUserId={data.handoverAllocatedUserId}
            handoverToUserId={data.handoverToUserId}
            customerName={data.customerName}
            customerLastOnlineTime={data.customerLastOnlineTime}
            isDepartmentView={isDepartmentView}
            showPin={showPin}
            onTogglePin={onTogglePin}
          />,
          ...(hasAgentColumn
            ? [
                <span
                  className={
                    data.agentName && onAgentNameClick
                      ? "cursor-pointer truncate underline transition-opacity hover:opacity-70"
                      : ""
                  }
                  onClick={(e) => {
                    if (data.agentName && onAgentNameClick) {
                      e.stopPropagation();
                      onAgentNameClick(data.agentName);
                    }
                  }}
                >
                  {data.agentName}
                </span>,
              ]
            : []),
          <span>{data.isPredictedToDoc ? <CircleCheck2Icon /> : null}</span>,
          <span className="truncate">{data.allocatedOn ? formatDateDaysOld(data.allocatedOn) : "-"}</span>,
          <span className="truncate">{data.conversationsOutboundCount ?? 0}</span>,
          <span className="truncate">{data.lastTouch ? formatDateDaysOld(data.lastTouch) : "-"}</span>,
          data.lastNote ? <LastNoteTooltip lastNote={data.lastNote} /> : "-",
          <ActivityHover activities={data.activities} lastTouch={data.lastTouch}>
            <span className="hidden text-[13px] 2xl:inline">{formatNewActivity(data.activities, data.lastTouch)}</span>
            <span className="text-[13px] 2xl:hidden">{formatNewActivityShort(data.activities, data.lastTouch)}</span>
          </ActivityHover>,
          <span className="truncate">{data.quotesTotalCount}</span>,
          <StatusChip status={data.quoteStatus || ""} hideIcon fullWidth className="!py-px" />,
          showPin ? (
            <ApplicationDropdown
              applicationId={data.applicationId?.toString()}
              isPinned={isPinned}
              isDepartmentView={isDepartmentView}
              onTogglePin={onTogglePin}
            />
          ) : (
            <span />
          ),
        ]}
      />
    </Row>
  );
};
